export const EUROPE_TIME_ZONE = "Europe/Madrid";

type TimeZoneDateParts = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
};

const formatterCache = new Map<string, Intl.DateTimeFormat>();

const getPartsFormatter = (timeZone: string) => {
    const cacheKey = `parts:${timeZone}`;
    const cached = formatterCache.get(cacheKey);

    if (cached !== undefined) {
        return cached;
    }

    const formatter = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    });

    formatterCache.set(cacheKey, formatter);
    return formatter;
};

const toNumber = (value: string | undefined): number => {
    const parsed = Number(value ?? "");
    return Number.isFinite(parsed) ? parsed : 0;
};

const toTimeZoneParts = (
    timestamp: number,
    timeZone: string = EUROPE_TIME_ZONE
): TimeZoneDateParts => {
    const formatter = getPartsFormatter(timeZone);
    const parts = formatter.formatToParts(new Date(timestamp));
    const lookup = new Map(parts.map((part) => [part.type, part.value]));

    return {
        year: toNumber(lookup.get("year")),
        month: toNumber(lookup.get("month")),
        day: toNumber(lookup.get("day")),
        hour: toNumber(lookup.get("hour")),
        minute: toNumber(lookup.get("minute")),
        second: toNumber(lookup.get("second")),
    };
};

const getTimeZoneOffsetMs = (timestamp: number, timeZone: string): number => {
    const normalizedTimestamp = Math.floor(timestamp / 1000) * 1000;
    const parts = toTimeZoneParts(normalizedTimestamp, timeZone);
    const asUtcTimestamp = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second,
        0
    );

    return asUtcTimestamp - normalizedTimestamp;
};

const toUtcTimestampFromTimeZoneParts = (
    parts: TimeZoneDateParts,
    timeZone: string = EUROPE_TIME_ZONE
): number => {
    const naiveUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second,
        0
    );

    let offset = getTimeZoneOffsetMs(naiveUtc, timeZone);
    let timestamp = naiveUtc - offset;
    const adjustedOffset = getTimeZoneOffsetMs(timestamp, timeZone);

    if (adjustedOffset !== offset) {
        offset = adjustedOffset;
        timestamp = naiveUtc - offset;
    }

    return timestamp;
};

const pad = (value: number) => String(value).padStart(2, "0");

const toTimeZoneInputDateTime = (
    timestamp: number,
    timeZone: string = EUROPE_TIME_ZONE
): string => {
    const parts = toTimeZoneParts(timestamp, timeZone);
    return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
};

const parseTimeZoneInputDateTime = (
    value: string,
    timeZone: string = EUROPE_TIME_ZONE
): number | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);

    if (match === null) {
        return null;
    }

    const [, year, month, day, hour, minute] = match;

    const parsedTimestamp = toUtcTimestampFromTimeZoneParts(
        {
            year: Number(year),
            month: Number(month),
            day: Number(day),
            hour: Number(hour),
            minute: Number(minute),
            second: 0,
        },
        timeZone
    );

    if (toTimeZoneInputDateTime(parsedTimestamp, timeZone) === value) {
        return parsedTimestamp;
    }

    for (let hoursOffset = -36; hoursOffset <= 36; hoursOffset += 1) {
        const candidate = parsedTimestamp + hoursOffset * 60 * 60 * 1000;
        if (toTimeZoneInputDateTime(candidate, timeZone) === value) {
            return candidate;
        }
    }

    return parsedTimestamp;
};

const getDaysInMonth = (year: number, month: number): number => {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
};

const subtractDashboardCalendarMonths = (
    timestamp: number,
    monthsToSubtract: number,
    timeZone: string = EUROPE_TIME_ZONE
): number => {
    const parts = toTimeZoneParts(timestamp, timeZone);
    const targetMonthIndex = parts.month - 1 - monthsToSubtract;
    const targetYear = parts.year + Math.floor(targetMonthIndex / 12);
    const targetMonth = ((targetMonthIndex % 12) + 12) % 12 + 1;
    const targetDay = Math.min(parts.day, getDaysInMonth(targetYear, targetMonth));

    return toUtcTimestampFromTimeZoneParts(
        {
            ...parts,
            year: targetYear,
            month: targetMonth,
            day: targetDay,
        },
        timeZone
    );
};

const getTimeZoneDayStartTimestamp = (
    referenceTimestamp: number,
    timeZone: string = EUROPE_TIME_ZONE
): number => {
    const parts = toTimeZoneParts(referenceTimestamp, timeZone);

    return toUtcTimestampFromTimeZoneParts(
        {
            year: parts.year,
            month: parts.month,
            day: parts.day,
            hour: 0,
            minute: 0,
            second: 0,
        },
        timeZone
    );
};

const shiftTimeZoneDays = (
    referenceTimestamp: number,
    days: number,
    timeZone: string = EUROPE_TIME_ZONE
): number => {
    const parts = toTimeZoneParts(referenceTimestamp, timeZone);

    return toUtcTimestampFromTimeZoneParts(
        {
            year: parts.year,
            month: parts.month,
            day: parts.day + days,
            hour: 0,
            minute: 0,
            second: 0,
        },
        timeZone
    );
};

const toTimeZoneInputDate = (
    timestamp: number,
    timeZone: string = EUROPE_TIME_ZONE
): string => {
    const parts = toTimeZoneParts(timestamp, timeZone);
    return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
};

const parseTimeZoneInputDate = (
    value: string,
    timeZone: string = EUROPE_TIME_ZONE
): number | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

    if (match === null) {
        return null;
    }

    const [, year, month, day] = match;

    return toUtcTimestampFromTimeZoneParts(
        {
            year: Number(year),
            month: Number(month),
            day: Number(day),
            hour: 0,
            minute: 0,
            second: 0,
        },
        timeZone
    );
};

const getTimeZoneMonthStartTimestamp = (
    referenceTimestamp: number,
    timeZone: string = EUROPE_TIME_ZONE
): number => {
    const parts = toTimeZoneParts(referenceTimestamp, timeZone);

    return toUtcTimestampFromTimeZoneParts(
        {
            year: parts.year,
            month: parts.month,
            day: 1,
            hour: 0,
            minute: 0,
            second: 0,
        },
        timeZone
    );
};

const getTimeZoneLastFullMonthRange = (
    referenceTimestamp: number,
    timeZone: string = EUROPE_TIME_ZONE
) => {
    const parts = toTimeZoneParts(referenceTimestamp, timeZone);
    const targetMonth = parts.month === 1 ? 12 : parts.month - 1;
    const targetYear = parts.month === 1 ? parts.year - 1 : parts.year;

    const from = toUtcTimestampFromTimeZoneParts(
        {
            year: targetYear,
            month: targetMonth,
            day: 1,
            hour: 0,
            minute: 0,
            second: 0,
        },
        timeZone
    );

    const currentMonthStart = toUtcTimestampFromTimeZoneParts(
        {
            year: parts.year,
            month: parts.month,
            day: 1,
            hour: 0,
            minute: 0,
            second: 0,
        },
        timeZone
    );

    return {
        from,
        to: currentMonthStart - 1,
    };
};

export const timezone = {
    toTimeZoneParts,
    toTimeZoneInputDateTime,
    parseTimeZoneInputDateTime,
    subtractDashboardCalendarMonths,
    getTimeZoneDayStartTimestamp,
    shiftTimeZoneDays,
    toTimeZoneInputDate,
    parseTimeZoneInputDate,
    getTimeZoneMonthStartTimestamp,
    getTimeZoneLastFullMonthRange,
};
